/**
 * Notification Card Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 526-477
 *
 * A card-style notification component with support for:
 * - Five style variants: Success, Warning, Error, Neutral, Info
 * - Title and description text
 * - Optional left and right icons
 * - Optional action buttons
 * - Full-width mode
 * - Color-coded by semantic meaning
 */

import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Icon, type IconName } from '../../icons';
import {
  colors,
  typography,
  spacing,
  componentSpacing,
  componentBorderRadius,
  componentShadows,
} from '../../tokens';

export interface NotificationCardProps {
  /**
   * Notification title
   * @default "Notification"
   */
  title?: string;

  /**
   * Notification description text
   * @default "Gripp gives growing operations a single platform to connect teams, track assets, and bring structure to the knowledge and routines that live in people's heads."
   */
  text?: string;

  /**
   * Style variant for semantic meaning
   * @default "success"
   */
  style?: 'success' | 'warning' | 'error' | 'neutral' | 'info';

  /**
   * Left icon name
   */
  leftIcon?: IconName;

  /**
   * Show left icon
   * @default false
   */
  showLeftIcon?: boolean;

  /**
   * Right icon name
   */
  rightIcon?: IconName;

  /**
   * Show right icon
   * @default false
   */
  showRightIcon?: boolean;

  /**
   * Show action buttons
   * @default true
   */
  showButtons?: boolean;

  /**
   * Primary button text
   * @default "Confirm"
   */
  primaryButtonText?: string;

  /**
   * Secondary button text
   * @default "Cancel"
   */
  secondaryButtonText?: string;

  /**
   * Callback when primary button is pressed
   */
  onPrimaryPress?: () => void;

  /**
   * Callback when secondary button is pressed
   */
  onSecondaryPress?: () => void;

  /**
   * Full width mode
   * @default false
   */
  full?: boolean;

  /**
   * Test ID for testing
   */
  testID?: string;
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  title = 'Notification',
  text = "Gripp gives growing operations a single platform to connect teams, track assets, and bring structure to the knowledge and routines that live in people's heads.",
  style = 'success',
  leftIcon = 'Add',
  showLeftIcon = false,
  rightIcon = 'X',
  showRightIcon = false,
  showButtons = true,
  primaryButtonText = 'Confirm',
  secondaryButtonText = 'Cancel',
  onPrimaryPress,
  onSecondaryPress,
  full = false,
  testID,
}) => {
  // Get background color using Crop Circle color tokens
  const getBackgroundColor = () => {
    switch (style) {
      case 'success':
        return colors.semantic.successLight; // #D3F4E7
      case 'warning':
        return colors.semantic.warningLight; // #FFF4C1
      case 'error':
        return colors.semantic.errorLight; // #FFD9D2
      case 'info':
        return colors.semantic.infoLight; // #D0E5F9
      case 'neutral':
      default:
        return colors.slate[100]; // #ECEEF1
    }
  };

  // Get icon color using Crop Circle color tokens
  const getIconColor = () => {
    switch (style) {
      case 'success':
        return colors.semantic.successDark; // #24835D
      case 'warning':
        return colors.semantic.warningDark; // #D7A321
      case 'error':
        return colors.semantic.error; // #E24D2A
      case 'info':
        return colors.semantic.infoDark; // #1F5B99
      case 'neutral':
      default:
        return colors.slate[600]; // #3A3F4D
    }
  };

  const backgroundColor = getBackgroundColor();
  const iconColor = getIconColor();

  const containerStyles = [
    styles.container,
    { backgroundColor },
    full && styles.containerFull,
  ];

  return (
    <View
      style={containerStyles}
      testID={testID}
    >
      {/* Header Section */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          {showLeftIcon && leftIcon && (
            <Icon name={leftIcon} size={16} color={iconColor} />
          )}
          <Text style={[styles.title, { color: iconColor }]}>{title}</Text>
        </View>
        {showRightIcon && rightIcon && (
          <Pressable hitSlop={8} accessibilityRole="button">
            <Icon name={rightIcon} size={16} color={iconColor} />
          </Pressable>
        )}
      </View>

      {/* Description Text */}
      <Text style={[styles.text, { color: iconColor }]}>{text}</Text>

      {/* Buttons Section */}
      {showButtons && (
        <View style={styles.buttonsContainer}>
          <Pressable
            style={[
              styles.button,
              styles.secondaryButton,
              { borderColor: iconColor },
            ]}
            onPress={onSecondaryPress}
            accessibilityRole="button"
          >
            <Text style={[styles.buttonText, { color: iconColor }]}>
              {secondaryButtonText}
            </Text>
          </Pressable>
          <Pressable
            style={[
              styles.button,
              styles.primaryButton,
              { backgroundColor: iconColor },
            ]}
            onPress={onPrimaryPress}
            accessibilityRole="button"
          >
            <Text
              style={[styles.buttonText, { color: colors.brand.white }]}
            >
              {primaryButtonText}
            </Text>
          </Pressable>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    minWidth: 320,
    maxWidth: 400,
    borderRadius: componentBorderRadius.card.default,
    padding: componentSpacing.card.padding,
    gap: componentSpacing.card.gap,
    ...componentShadows.card.default,
  },
  containerFull: {
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm, // 12px
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm, // 12px
    flex: 1,
  },
  title: {
    ...typography.title.smallEmphasised,
  },
  text: {
    ...typography.body.small,
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: spacing.sm, // 12px
    marginTop: spacing.xs, // 8px
  },
  button: {
    minHeight: 40,
    paddingVertical: spacing.xs,
    paddingHorizontal: componentSpacing.button.paddingHorizontal,
    borderRadius: componentBorderRadius.button.small,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryButton: {
    flex: 1,
  },
  secondaryButton: {
    flex: 1,
    backgroundColor: 'transparent',
    borderWidth: 1,
  },
  buttonText: {
    ...typography.body.smallEmphasised,
  },
});

NotificationCard.displayName = 'NotificationCard';
